import React, { createContext, useContext, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const AccessContext = createContext(null);

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AccessProvider = ({ children }) => {
  const [showAccess, setShowAccess]         = useState(false);
  const [accessTab, setAccessTab]           = useState('login');
  const [accessUsername, setAccessUsername] = useState('');
  const [accessPassword, setAccessPassword] = useState('');
  const [accessError, setAccessError]       = useState('');
  const [accessSuccess, setAccessSuccess]   = useState('');
  const [accessLoading, setAccessLoading]   = useState(false);
  const [currentUser, setCurrentUser]       = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/api/auth/me/`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setCurrentUser(data); })
        .catch(() => {});
    }
  }, []);

  const openAccess = (tab = 'login') => {
    setAccessTab(tab);
    setAccessError('');
    setAccessSuccess('');
    setAccessUsername('');
    setAccessPassword('');
    setShowAccess(true);
  };

  const closeAccess = () => {
    setShowAccess(false);
    setAccessError('');
    setAccessSuccess('');
    setAccessUsername('');
    setAccessPassword('');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setCurrentUser(null);
  };

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    setAccessError('');
    setAccessSuccess('');
    setAccessLoading(true);

    try {
      if (accessTab === 'login') {
        const res = await fetch(`${API}/api/auth/login/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: accessUsername, password: accessPassword })
        });
        const data = await res.json();
        if (!res.ok) {
          setAccessError(data.detail || 'Invalid username or password');
        } else {
          localStorage.setItem('token', data.access);
          localStorage.setItem('refresh_token', data.refresh);
          const meRes = await fetch(`${API}/api/auth/me/`, {
            headers: { Authorization: `Bearer ${data.access}` }
          });
          const me = await meRes.json();
          setCurrentUser(me);
          if (window.gtag) {
            window.gtag('event', 'login', {
              method: 'Access Modal',
              user_role: me.role || 'unknown'
            });
          }
          closeAccess();
        }
      } else {
        const res = await fetch(`${API}/api/public/register/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: accessUsername, password: accessPassword })
        });
        const data = await res.json();
        if (!res.ok) {
          setAccessError(data.error || 'Registration failed');
        } else {
          if (window.gtag) {
            window.gtag('event', 'sign_up', {
              method: 'Access Modal',
              user_role: 'public'
            });
          }
          setAccessSuccess('Account created! You can now log in.');
          setAccessTab('login');
          setAccessUsername('');
          setAccessPassword('');
        }
      }
    } catch {
      setAccessError('Something went wrong. Please try again.');
    } finally {
      setAccessLoading(false);
    }
  };

  return (
    <AccessContext.Provider value={{ currentUser, setCurrentUser, openAccess, closeAccess, handleLogout, isLoggedIn: !!currentUser }}>
      {children}

      <AnimatePresence>
        {showAccess && (
          <motion.div
            key="access-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center px-3 sm:px-6 md:px-8"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}
            onClick={closeAccess}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0,  scale: 1    }}
              exit={{   opacity: 0, y: 24, scale: 0.96  }}
              transition={{ duration: 0.35, ease: [0.33, 0.1, 0.2, 1] }}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 896,
                minHeight: 400,
                background: 'rgba(0,0,0,0.8)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                padding: 'clamp(24px, 5vw, 64px) clamp(16px, 5vw, 48px)',
                position: 'relative',
                boxShadow: '0 32px 80px rgba(0,0,0,0.8)',
                overflowY: 'auto',
                maxHeight: '90vh',
              }}
            >
              <button
                onClick={closeAccess}
                style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
              >
                ×
              </button>

            

              

              
              <form onSubmit={handleAccessSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <input
                  type="text"
                  placeholder="Username"
                  value={accessUsername}
                  onChange={e => setAccessUsername(e.target.value)}
                  required
                  style={{
                    background: 'transparent',
                    border: '1px solid white', borderRadius: 9999,
                    padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 14px)', color: 'white',
                    fontFamily: "'Poppins', sans-serif", fontSize: 'clamp(11px, 1.5vw, 13px)',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={accessPassword}
                  onChange={e => setAccessPassword(e.target.value)}
                  required
                  style={{
                    background: 'transparent',
                    border: '1px solid white', borderRadius: 9999,
                    padding: '12px 14px', color: 'white',
                    fontFamily: "'Poppins', sans-serif", fontSize: 13,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />

                {accessError && (
                  <p style={{ color: '#ff6b6b', fontSize: 12, fontFamily: "'Poppins', sans-serif", margin: 0 }}>
                    {accessError}
                  </p>
                )}
                {accessSuccess && (
                  <p style={{ color: '#A47F2D', fontSize: 12, fontFamily: "'Poppins', sans-serif", margin: 0 }}>
                    {accessSuccess}
                  </p>
                )}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
                <motion.button
                  type="submit"
                  disabled={accessLoading}
                  whileHover={{ opacity: 0.9 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAccessTab('login')}
                  style={{
                    background: accessTab === 'login' ? '#A47F2D' : 'rgba(255,255,255,0.1)',
                    border: 'none', borderRadius: 9999, padding: 'clamp(8px, 2vw, 10px) clamp(20px, 4vw, 32px)',
                    color: accessTab === 'login' ? '#000' : 'rgba(255,255,255,0.4)',
                    // fontFamily: "'Ojuju', sans-serif",
                    fontSize: 14, fontWeight: 'bold', letterSpacing: '0.08em',
                    cursor: accessLoading ? 'not-allowed' : 'pointer',
                    opacity: accessLoading ? 0.7 : 1,
                  }}
                >
                  {accessLoading && accessTab === 'login' ? '...' : 'Log In'}
                </motion.button>

                <motion.button
                  type="submit"
                  disabled={accessLoading}
                  whileHover={{ opacity: 0.9 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAccessTab('signup')}
                  style={{
                    background: accessTab === 'signup' ? '#A47F2D' : 'rgba(255,255,255,0.1)',
                    border: 'none', borderRadius: 9999, padding: 'clamp(8px, 2vw, 10px) clamp(20px, 4vw, 32px)',
                    color: accessTab === 'signup' ? '#000' : 'rgba(255,255,255,0.4)',
                    // fontFamily: "'Ojuju', sans-serif",
                    fontSize: 14, fontWeight: 'bold', letterSpacing: '0.08em',
                    cursor: accessLoading ? 'not-allowed' : 'pointer',
                    opacity: accessLoading ? 0.7 : 1,
                  }}
                >
                  {accessLoading && accessTab === 'signup' ? '...' : 'Sign Up'}
                </motion.button>
              </div>
              </form>

             

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.2)' }} />
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: "'Poppins', sans-serif" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.2)' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                {['Google', 'Apple', 'Email'].map(provider => (
                  <button
                    key={provider}
                    type="button"
                    disabled
                    style={{
                      background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.35)',
                      border: 'none', borderRadius: 9999,
                      padding: '8px 16px', cursor: 'not-allowed',
                      fontFamily: "'Poppins', sans-serif", fontSize: 11,
                      minWidth: 110,
                    }}
                  >
                    {provider}
                  </button>
                ))}
              </div>

              

              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontFamily: "'Poppins', sans-serif", marginTop: 20, lineHeight: 1.6, textAlign: 'center' }}>
                If you're a DAAT partner, use your partner credentials to log in.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AccessContext.Provider>
  );
};

export const useAccess = () => {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error('useAccess must be used within AccessProvider');
  return ctx;
};